// Scan service — singular current-scan trigger, status/result reads, and category overrides.
//
// Pairing: this service is consumed by `useScanResultQuery`,
// `useScanDetailQuery`, `useTriggerScanMutation`,
// and `useOverrideScanCategoryMutation`.
//
// Scan history lives in sibling `scans.service.js`.

import { httpService } from 'src/services/app/http.service'
import { BANNER_ENDPOINTS } from 'src/services/consts/api.const'
import {
  normalizeScanResults,
  normalizeScanStatus,
  normalizeScanType,
} from 'src/services/helpers/scan/status.helper'

export const scanService = {
  triggerScan,
  getLatestScanResult,
  getScanById,
  overrideScanCategory,
}

/**
 * @param {string} siteId
 * @param {{ scanType?: string }} [options]
 * @returns {Promise<ScanStatus|null>}
 * @throws on HTTP 404 (site gone) and other non-recoverable API errors
 */
async function triggerScan(siteId, { scanType } = {}) {
  const body = { scanType: normalizeScanType(scanType) }
  return resolveScanStatus(() =>
    httpService.post(`${BANNER_ENDPOINTS.SITES}/${siteId}/scans`, body)
  )
}

/**
 * Latest scan for a site.
 * HTTP 200 + null/empty → `null` (never scanned). HTTP 404 → throws (site deleted).
 *
 * @param {string} siteId
 * @returns {Promise<ScanStatus|null>}
 * @throws on HTTP 404 (site gone) and other non-recoverable API errors
 */
async function getLatestScanResult(siteId) {
  return resolveScanStatus(() =>
    httpService.get(`${BANNER_ENDPOINTS.SITES}/${siteId}/scans/latest`, {
      acceptErrorPayload: true,
    })
  )
}

/**
 * @param {string} siteId
 * @param {string} scanId
 * @returns {Promise<ScanStatus|null>}
 * @throws on HTTP 404 (site/scan missing) and other non-recoverable API errors
 */
async function getScanById(siteId, scanId) {
  return resolveScanStatus(() =>
    httpService.get(`${BANNER_ENDPOINTS.SITES}/${siteId}/scans/${scanId}`)
  )
}

/**
 * Override category classifications for cookies, web storage (indexedDB,
 * localStorage, sessionStorage), and scripts in a scan result.
 *
 * Payload is {@link OverrideScanCategoryPayload} — top-level `cookies`, `webStorage`,
 * and `scripts` arrays on the wire (not a unified `trackers` array). The UI builds
 * those arrays; this method passes `payload` through unchanged.
 *
 * PATCH returns `{ scanId, updated, results: { cookies, webStorage, scripts, … } }`.
 * Response `results` is normalized via `normalizeScanResults` into {@link ScanResults}
 * (`trackers[]`, not raw `results.cookies`).
 *
 * @param {string} siteId
 * @param {string} scanId
 * @param {OverrideScanCategoryPayload} payload
 * @returns {Promise<ScanResults>}
 */
async function overrideScanCategory(siteId, scanId, payload) {
  const res = await httpService.patch(
    `${BANNER_ENDPOINTS.SITES}/${siteId}/scans/${scanId}`,
    payload
  )
  const results = res?.results
  if (!results || typeof results !== 'object') {
    throw new Error('overrideScanCategory: invalid response from server')
  }
  return normalizeScanResults(results)
}

/**
 * Normalize scan read/trigger responses.
 *
 * - HTTP 200 with null/empty body → `null` (no scan yet for `/scans/latest`)
 * - HTTP 200 (or rejected body) with domain `error` payload → normalized scan when possible
 * - HTTP 404 → rethrown (site/resource gone — not the same as “no scans”)
 *
 * "No scan yet" is a normal 200 with an empty/status-less body — `normalizeScanStatus`
 * already resolves that to `null` on the success path. A thrown 404 here always means
 * the site itself is missing, inactive, or not the caller's, so it's a real error and
 * must propagate (never swallowed to `null`, which would misread as "never scanned").
 *
 * @param {() => Promise<unknown>} request
 * @returns {Promise<ScanStatus|null>}
 */
async function resolveScanStatus(request) {
  try {
    const res = await request()
    return normalizeScanStatus(res)
  } catch (error) {
    // 404 must propagate — callers distinguish deleted site/missing resource from null.
    if (error?.response?.status === 404) throw error
    const normalized = normalizeScanStatus(error?.response?.data)
    if (normalized) return normalized
    throw error
  }
}
